/**
 * PortfolioDashboard — summary dashboard with 6 chart cards.
 *
 * Cards:
 * 1. Ring Distribution — donut chart
 * 2. Axis Radar — spider chart
 * 3. Phase Pipeline — horizontal stacked bar
 * 4. Status Summary — metric cards
 * 5. Initiative Health — heatmap table
 * 6. Cross-Axis Dependency Heatmap
 */

import { useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type {
  AdjacencyNode,
  RelatednessRing,
  AdjacencyAxis,
  Status,
  StrategicInitiative,
} from '../../types/adjacency';
import { RING_SUCCESS_RATES } from '../../types/adjacency';
import { INITIATIVE_COLORS, STATUS_COLORS, STATUS_DEFAULT_COLOR } from '../../utils/map-colors';
import { AXIS_COLORS, PHASE_COLORS } from '../../utils/view-colors';
import { getAllPhases, getAllStatuses } from '../../utils/reference-data';
import { nodePassesFilter } from '../../utils/filter-nodes';
import { countAxisConnections } from '../../utils/connections';

// ── Constants ────────────────────────────────────────────────────────

const RING_COLORS: Record<RelatednessRing, string> = {
  0: '#3b82f6',
  1: '#60a5fa',
  2: '#93c5fd',
  3: '#bfdbfe',
  4: '#6b7280',
};

const ALL_AXES: AdjacencyAxis[] = [
  'New Services', 'New Markets & Customers', 'Forward Integration',
  'New Technologies & Capabilities', 'New Channels', 'New Geographies',
  'Backward Integration', 'New Products',
];


const ALL_INITIATIVES: StrategicInitiative[] = [
  'US Mining', 'Proximity Detection', 'Analytics', 'Maritime',
  'International Mining', 'New Verticals', 'Capabilities', 'Other',
];

const RINGS: RelatednessRing[] = [0, 1, 2, 3, 4];

// ── Component ────────────────────────────────────────────────────────

export function PortfolioDashboard() {
  const { nodes, filters } = useAdjacencyStore();

  const filtered = useMemo(
    () => nodes.filter((n) => nodePassesFilter(n, filters)),
    [nodes, filters],
  );

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 auto-rows-min">
        <Card title="Ring Distribution">
          <RingDistribution nodes={filtered} />
        </Card>
        <Card title="Axis Radar">
          <AxisRadar nodes={filtered} />
        </Card>
        <Card title="Phase Pipeline">
          <PhasePipeline nodes={filtered} />
        </Card>
        <Card title="Status Summary">
          <StatusSummary nodes={filtered} />
        </Card>
        <Card title="Initiative Health">
          <InitiativeHealth nodes={filtered} />
        </Card>
        <Card title="Cross-Axis Dependencies">
          <CrossAxisHeatmap nodes={filtered} />
        </Card>
      </div>
    </div>
  );
}

// ── Card wrapper ─────────────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass-panel p-4 rounded-lg flex flex-col min-h-[280px]">
      <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
        {title}
      </h3>
      <div className="flex-1 min-h-0">
        {children}
      </div>
    </div>
  );
}

// ── 1. Ring Distribution (Donut) ─────────────────────────────────────

function RingDistribution({ nodes }: { nodes: AdjacencyNode[] }) {
  const data = useMemo(() => {
    const counts = new Map<RelatednessRing, number>();
    for (const r of RINGS) counts.set(r, 0);
    for (const n of nodes) counts.set(n.relatednessRing, (counts.get(n.relatednessRing) ?? 0) + 1);
    return RINGS.map((r) => ({
      name: `${r} — ${RING_SUCCESS_RATES[r].label}`,
      value: counts.get(r) ?? 0,
      fill: RING_COLORS[r],
    })).filter((d) => d.value > 0);
  }, [nodes]);

  if (data.length === 0) return <Empty />;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={45}
          outerRadius={75}
          paddingAngle={2}
          dataKey="value"
          strokeWidth={0}
        >
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Pie>
        <RTooltip
          contentStyle={{ background: 'var(--modal-bg-heavy)', border: '1px solid var(--modal-border)', borderRadius: 8, fontSize: 11 }}
          itemStyle={{ color: 'var(--text-primary)' }}
        />
        <Legend
          wrapperStyle={{ fontSize: 10, color: 'var(--text-muted)' }}
          formatter={(value: string) => <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── 2. Axis Radar ────────────────────────────────────────────────────

function AxisRadar({ nodes }: { nodes: AdjacencyNode[] }) {
  const data = useMemo(() => {
    const counts = new Map<AdjacencyAxis, number>();
    for (const a of ALL_AXES) counts.set(a, 0);
    for (const n of nodes) {
      if (n.adjacencyAxis !== 'Core') counts.set(n.adjacencyAxis, (counts.get(n.adjacencyAxis) ?? 0) + 1);
    }
    return ALL_AXES.map((a) => ({
      axis: a === 'New Technologies & Capabilities' ? 'New Tech' : a,
      count: counts.get(a) ?? 0,
    }));
  }, [nodes]);

  return (
    <ResponsiveContainer width="100%" height={220}>
      <RadarChart data={data}>
        <PolarGrid stroke="rgba(255,255,255,0.1)" />
        <PolarAngleAxis
          dataKey="axis"
          tick={{ fill: 'var(--text-muted)', fontSize: 9 }}
        />
        <PolarRadiusAxis
          tick={{ fill: 'var(--text-dim)', fontSize: 8 }}
          axisLine={false}
        />
        <Radar
          dataKey="count"
          stroke="#3b82f6"
          fill="#3b82f6"
          fillOpacity={0.25}
          strokeWidth={2}
        />
        <RTooltip
          contentStyle={{ background: 'var(--modal-bg-heavy)', border: '1px solid var(--modal-border)', borderRadius: 8, fontSize: 11 }}
          itemStyle={{ color: 'var(--text-primary)' }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ── 3. Phase Pipeline (Horizontal Stacked Bar) ───────────────────────

function PhasePipeline({ nodes }: { nodes: AdjacencyNode[] }) {
  const phases = useMemo(() => getAllPhases(nodes), [nodes]);
  const data = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of phases) counts[p] = 0;
    for (const n of nodes) {
      if (n.phase) counts[n.phase] = (counts[n.phase] ?? 0) + 1;
    }
    return [counts]; // Single bar with stacked segments
  }, [nodes, phases]);

  return (
    <ResponsiveContainer width="100%" height={80}>
      <BarChart data={data} layout="vertical" barSize={30}>
        <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.05)" />
        <XAxis type="number" tick={{ fill: 'var(--text-dim)', fontSize: 10 }} axisLine={false} />
        <YAxis type="category" dataKey={() => ''} hide />
        {phases.map((phase) => (
          <Bar
            key={phase}
            dataKey={phase}
            stackId="a"
            fill={PHASE_COLORS[phase] ?? '#6b7280'}
            activeBar={{ stroke: PHASE_COLORS[phase] ?? '#6b7280', strokeWidth: 2, filter: `drop-shadow(0 0 6px ${PHASE_COLORS[phase] ?? '#6b7280'}80)` }}
          />
        ))}
        <RTooltip
          cursor={false}
          contentStyle={{ background: 'var(--modal-bg-heavy)', border: '1px solid var(--modal-border)', borderRadius: 8, fontSize: 11, boxShadow: 'var(--modal-shadow)' }}
          labelStyle={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 11 }}
          itemStyle={{ color: 'var(--text-secondary)' }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── 4. Status Summary (Metric Cards) ─────────────────────────────────

function StatusSummary({ nodes }: { nodes: AdjacencyNode[] }) {
  const statuses = useMemo(() => getAllStatuses(nodes), [nodes]);
  const counts = useMemo(() => {
    const map = new Map<Status, number>();
    for (const s of statuses) map.set(s, 0);
    for (const n of nodes) {
      if (n.status) map.set(n.status, (map.get(n.status) ?? 0) + 1);
    }
    return map;
  }, [nodes, statuses]);

  const total = nodes.length || 1;

  return (
    <div className="grid grid-cols-2 gap-2">
      {statuses.map((status) => {
        const count = counts.get(status) ?? 0;
        const pct = ((count / total) * 100).toFixed(0);
        const color = STATUS_COLORS[status] ?? STATUS_DEFAULT_COLOR;
        return (
          <div
            key={status}
            className="rounded-lg p-2.5 flex items-start gap-2"
            style={{ background: color + '12' }}
          >
            <span
              className="w-2 h-2 rounded-full mt-1 shrink-0"
              style={{ background: color }}
            />
            <div>
              <div className="text-lg font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                {count}
              </div>
              <div className="text-[10px] leading-tight" style={{ color: 'var(--text-muted)' }}>
                {status} ({pct}%)
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── 5. Initiative Health (Heatmap Table) ─────────────────────────────

function InitiativeHealth({ nodes }: { nodes: AdjacencyNode[] }) {
  const data = useMemo(() => {
    const grid = new Map<string, Map<RelatednessRing, number>>();
    for (const init of ALL_INITIATIVES) {
      grid.set(init, new Map(RINGS.map((r) => [r, 0])));
    }
    for (const n of nodes) {
      const row = grid.get(n.strategicInitiative);
      if (row) row.set(n.relatednessRing, (row.get(n.relatednessRing) ?? 0) + 1);
    }
    return grid;
  }, [nodes]);

  const maxCount = useMemo(() => {
    let max = 1;
    for (const row of data.values()) {
      for (const v of row.values()) {
        if (v > max) max = v;
      }
    }
    return max;
  }, [data]);

  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr>
            <th />
            <th colSpan={RINGS.length} className="px-1 pt-1 pb-0 text-center font-semibold text-[9px] uppercase tracking-widest" style={{ color: 'var(--text-dim)' }}>
              Relatedness Rings
            </th>
          </tr>
          <tr>
            <th className="text-left px-1 py-1 font-medium" style={{ color: 'var(--text-muted)' }}>Initiative</th>
            {RINGS.map((r) => (
              <th key={r} className="px-1 py-1 text-center font-medium" style={{ color: 'var(--text-muted)' }}>
                R{r}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ALL_INITIATIVES.map((init) => {
            const row = data.get(init)!;
            const total = RINGS.reduce<number>((s, r) => s + (row.get(r) ?? 0), 0);
            if (total === 0) return null;
            return (
              <tr key={init}>
                <td className="px-1 py-1 whitespace-nowrap" style={{ color: INITIATIVE_COLORS[init] }}>
                  {init}
                </td>
                {RINGS.map((r) => {
                  const v = row.get(r) ?? 0;
                  const intensity = v / maxCount;
                  return (
                    <td
                      key={r}
                      className="px-1 py-1 text-center tabular-nums"
                      style={{
                        background: v > 0 ? `rgba(59,130,246,${(intensity * 0.5 + 0.05).toFixed(2)})` : 'transparent',
                        color: v > 0 ? 'var(--text-primary)' : 'var(--text-dim)',
                      }}
                    >
                      {v || '·'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── 6. Cross-Axis Dependency Heatmap ─────────────────────────────────

function CrossAxisHeatmap({ nodes }: { nodes: AdjacencyNode[] }) {
  // WQ-343: each connection counted once (mirrored pairs share one key)
  const grid = useMemo(() => countAxisConnections(nodes, ALL_AXES), [nodes]);

  const maxCount = useMemo(() => {
    let max = 1;
    for (const row of grid.values()) {
      for (const v of row.values()) {
        if (v > max) max = v;
      }
    }
    return max;
  }, [grid]);

  const shortLabel = (axis: string) => {
    if (axis === 'New Technologies & Capabilities') return 'Tech';
    if (axis === 'New Markets & Customers') return 'Markets';
    if (axis === 'Forward Integration') return 'Fwd Int';
    if (axis === 'Backward Integration') return 'Bkwd Int';
    if (axis === 'New Geographies') return 'Geo';
    if (axis === 'New Channels') return 'Channels';
    if (axis === 'New Services') return 'Services';
    if (axis === 'New Products') return 'Products';
    return axis;
  };

  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <table className="w-full border-collapse text-[9px]">
        <thead>
          <tr>
            <th className="px-1 py-1" />
            {ALL_AXES.map((a) => (
              <th
                key={a}
                className="px-1 py-1 text-center font-medium"
                style={{ color: AXIS_COLORS[a], writingMode: 'vertical-lr', transform: 'rotate(180deg)', maxHeight: 60 }}
              >
                {shortLabel(a)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ALL_AXES.map((rowAxis) => (
            <tr key={rowAxis}>
              <td className="px-1 py-1 whitespace-nowrap font-medium" style={{ color: AXIS_COLORS[rowAxis] }}>
                {shortLabel(rowAxis)}
              </td>
              {ALL_AXES.map((colAxis) => {
                const v = grid.get(rowAxis)?.get(colAxis) ?? 0;
                const intensity = v / maxCount;
                const isDiag = rowAxis === colAxis;
                return (
                  <td
                    key={colAxis}
                    className="px-1 py-1 text-center tabular-nums"
                    style={{
                      background: v > 0
                        ? isDiag
                          ? `rgba(59,130,246,${(intensity * 0.6 + 0.1).toFixed(2)})`
                          : `rgba(245,158,11,${(intensity * 0.5 + 0.05).toFixed(2)})`
                        : 'transparent',
                      color: v > 0 ? 'var(--text-primary)' : 'var(--text-dim)',
                      minWidth: 24,
                    }}
                  >
                    {v || '·'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-[9px]" style={{ color: 'var(--text-dim)' }}>
        Connections between axes — each link counted once
      </p>
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────

function Empty() {
  return (
    <div className="flex items-center justify-center h-full">
      <span className="text-xs" style={{ color: 'var(--text-dim)' }}>No data</span>
    </div>
  );
}
